import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { GripVertical, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { SECTION_DROP_PREFIX } from '../lib/surumMutations'

type SortableBlockProps = {
  id: string
  zoom: number
  compact?: boolean
  className?: string
  removeLabel?: string
  onRemove?: () => void
  children: ReactNode
}

const controlClass =
  'flex size-6 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-xs transition-colors'

export function SortableBlock({ id, zoom, compact, className, removeLabel, onRemove, children }: SortableBlockProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      data-block-id={id}
      style={{
        transform: transform ? `translate3d(${transform.x / zoom}px, ${transform.y / zoom}px, 0)` : undefined,
        transition,
      }}
      className={cn(
        'group/block relative min-w-0 rounded-md outline-offset-4 transition-[outline-color]',
        'outline outline-1 outline-dashed outline-transparent hover:outline-primary/30',
        isDragging && 'z-20 bg-card opacity-90 shadow-[0_12px_28px_-8px_color-mix(in_oklab,var(--shadow-color)_35%,transparent)] outline-solid outline-primary/50',
        className,
      )}
    >
      <div
        className={cn(
          'absolute z-10 flex gap-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/block:opacity-100',
          compact ? '-top-3 right-2' : '-left-8 top-0.5 flex-col',
          isDragging && 'opacity-100',
        )}
      >
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label={t('hr.belgeler.canvas.dragBlock')}
          {...attributes}
          {...listeners}
          className={cn(controlClass, 'cursor-grab touch-none hover:border-primary/40 hover:text-primary active:cursor-grabbing', isDragging && 'text-primary')}
        >
          <GripVertical className="size-3.5" />
        </button>
        {onRemove ? (
          <button
            type="button"
            aria-label={removeLabel}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className={cn(controlClass, 'hover:border-destructive/40 hover:bg-destructive/[0.06] hover:text-destructive', isDragging && 'hidden')}
          >
            <Trash2 className="size-3.5" />
          </button>
        ) : null}
      </div>
      {children}
    </div>
  )
}

export function SectionDropZone({ sectionId, empty, children }: { sectionId: string; empty: boolean; children: ReactNode }) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: `${SECTION_DROP_PREFIX}${sectionId}`, disabled: !empty })

  if (!empty) return <section className="grid grid-cols-6 gap-x-3 gap-y-2">{children}</section>

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'flex min-h-12 items-center justify-center rounded-md border border-dashed text-[11px] text-muted-foreground transition-colors',
        isOver ? 'border-primary bg-primary/[0.05] text-primary' : 'border-border',
      )}
    >
      {t('hr.belgeler.canvas.dropHere')}
    </section>
  )
}
